import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Download, Upload, Send, CheckCircle2, Circle, Eye, Sparkles, ArrowDownToLine, ArrowUpFromLine, FileX2 } from "lucide-react";

const TAB_TRIGGER = "rounded-none bg-transparent px-0 pb-3 text-sm font-medium text-[#1C1C1E]/55 hover:text-[#1C1C1E] transition-colors duration-200 data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:text-[#1C1C1E] data-[state=active]:border-b-2 data-[state=active]:border-[#00823B] border-b-2 border-transparent whitespace-nowrap";

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const clientId = user?.client_id;
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [files, setFiles] = useState([]);
  const [team, setTeam] = useState([]);
  const [comments, setComments] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [recommendation, setRecommendation] = useState(null);

  const load = async () => {
    if (!clientId) return;
    const [c, tk, inv, cl, fl, tm, cm, rec] = await Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/tasks`),
      api.get(`/clients/${clientId}/invoices`),
      api.get(`/clients/${clientId}/checklists`),
      api.get(`/clients/${clientId}/files`),
      api.get(`/clients/${clientId}/team`),
      api.get(`/clients/${clientId}/comments`),
      api.get(`/clients/${clientId}/portal-recommendation`),
    ]);
    setClient(c.data);
    setTasks(tk.data);
    setInvoices(inv.data);
    setChecklists(cl.data);
    setFiles(fl.data);
    setTeam(tm.data);
    setComments(cm.data);
    setRecommendation(rec.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clients/${clientId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/clients/${clientId}/comments`, { message: newMessage });
      setNewMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="portal-loading">
        <div className="h-8 w-8 border-2 border-[#76C121] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const deliveredFiles = files.filter((f) => f.category !== "Received from Client");
  const receivedFiles = files.filter((f) => f.category === "Received from Client");

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="client-portal-dashboard">
      <header className="sticky top-0 z-30 bg-white border-b border-[#1C1C1E]/10 flex flex-wrap items-center justify-between px-6 lg:px-8 py-4">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_b49b5f10-3087-4d0f-adce-b804118659f8/artifacts/bpbmqyb1_ENVIROWEALTH%20LOGO%20%282%29.jpg"
            alt="EnviroWealth"
            className="h-7 object-contain"
          />
          <div className="h-6 w-px bg-[#1C1C1E]/10" />
          <div>
            <p data-testid="portal-company-name" className="font-heading text-sm font-medium text-[#1C1C1E]">{client.company_name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#1C1C1E]/45">Client Portal</p>
          </div>
        </div>
        <Button data-testid="portal-logout-button" variant="outline" size="sm" onClick={logout} className="gap-1.5 rounded-sm active:scale-[0.98] transition-transform duration-150">
          <LogOut className="h-3.5 w-3.5" /> Logout
        </Button>
      </header>

      <main className="px-6 lg:px-8 py-8 max-w-[1240px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-[#1C1C1E]">Welcome, {user.name}</h1>
            <p className="text-sm text-[#1C1C1E]/60 mt-1">{client.engagement_scope}</p>
          </div>
          <StatusBadge value={client.status} testId="portal-client-status" />
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="w-full h-auto justify-start bg-transparent p-0 border-b border-[#1C1C1E]/10 flex gap-6 overflow-x-auto rounded-none">
            <TabsTrigger value="overview" data-testid="portal-tab-overview" className={TAB_TRIGGER}>Overview</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="portal-tab-tasks" className={TAB_TRIGGER}>Project Progress</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="portal-tab-invoices" className={TAB_TRIGGER}>Invoices</TabsTrigger>
            <TabsTrigger value="checklists" data-testid="portal-tab-checklists" className={TAB_TRIGGER}>Checklists</TabsTrigger>
            <TabsTrigger value="files" data-testid="portal-tab-files" className={TAB_TRIGGER}>Files</TabsTrigger>
            <TabsTrigger value="team" data-testid="portal-tab-team" className={TAB_TRIGGER}>Your Team</TabsTrigger>
            <TabsTrigger value="messages" data-testid="portal-tab-messages" className={TAB_TRIGGER}>Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-5">
            <Panel>
              <div className="grid grid-cols-2 gap-5 p-5 text-sm">
                <Field label="POC Name" value={client.poc_name} />
                <Field label="POC Email" value={client.poc_email} />
                <Field label="Contract Start" value={client.contract_start_date} />
                <Field label="Project End" value={client.project_end_date} />
              </div>
            </Panel>
            {recommendation && (
              <div className="border border-[#76C121]/25 bg-[#F7FEE7] rounded-sm" data-testid="portal-ai-recommendation">
                <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#76C121]/20">
                  <Sparkles className="h-4 w-4 text-[#00823B]" />
                  <h3 className="font-heading text-sm font-medium text-[#1C1C1E]">Recommendations for Your Next Year</h3>
                </div>
                <p className="text-sm text-[#1C1C1E]/80 whitespace-pre-wrap leading-relaxed p-5">{recommendation.content}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tasks" className="mt-6 space-y-2">
            {tasks.length === 0 && <EmptyState text="No project tasks logged yet." />}
            {tasks.map((t) => (
              <div key={t.id} data-testid={`portal-task-row-${t.id}`} className="bg-white border border-[#1C1C1E]/10 rounded-sm p-4 flex items-center justify-between hover:bg-[#1C1C1E]/[0.015] transition-colors duration-200">
                <div>
                  <p className="text-sm font-medium text-[#1C1C1E]">{t.title}</p>
                  {t.deadline && <p className="font-mono text-[11px] text-[#1C1C1E]/45 mt-1 uppercase tracking-wide">Due {t.deadline}</p>}
                </div>
                <StatusBadge value={t.status} />
              </div>
            ))}
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <Panel noPadding>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1C1C1E]/[0.02] border-b border-[#1C1C1E]/10">
                    <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-[#1C1C1E]/50">Invoice #</th>
                    <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-[#1C1C1E]/50">Amount</th>
                    <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-[#1C1C1E]/50">Due</th>
                    <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-[#1C1C1E]/50">Status</th>
                    <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-[#1C1C1E]/50">File</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#1C1C1E]/45 text-sm">No invoices yet.</td></tr>}
                  {invoices.map((inv) => (
                    <tr key={inv.id} data-testid={`portal-invoice-row-${inv.id}`} className="border-b border-[#1C1C1E]/5 last:border-0 hover:bg-[#1C1C1E]/[0.02] transition-colors duration-200">
                      <td className="px-4 py-3 font-mono text-xs font-medium text-[#1C1C1E]">{inv.invoice_number}</td>
                      <td className="px-4 py-3 font-medium text-[#1C1C1E]">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-[#1C1C1E]/70">{inv.due_date}</td>
                      <td className="px-4 py-3"><StatusBadge value={inv.status} /></td>
                      <td className="px-4 py-3">
                        {inv.file_path ? (
                          <div className="flex items-center gap-2.5">
                            <a href={fileUrl(inv.file_path)} target="_blank" rel="noreferrer" data-testid={`portal-invoice-view-${inv.id}`} title="View"><Eye className="h-4 w-4 text-[#1C1C1E]/45" /></a>
                            <a href={downloadUrl(inv.file_path, inv.invoice_number)} data-testid={`portal-invoice-download-${inv.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                          </div>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </TabsContent>

          <TabsContent value="checklists" className="mt-6 space-y-4">
            {checklists.length === 0 && <EmptyState text="No checklists assigned yet." />}
            {checklists.map((cl) => {
              const done = cl.items.filter((i) => i.completed).length;
              return (
                <Panel key={cl.id} testId={`portal-checklist-${cl.id}`}>
                  <div className="flex items-center justify-between p-4 border-b border-[#1C1C1E]/10 bg-[#F8F9FA]">
                    <p className="font-heading text-sm font-medium text-[#1C1C1E]">{cl.template_name}</p>
                    <span className="font-mono text-[11px] text-[#1C1C1E]/50 tracking-wide">{done}/{cl.items.length} COMPLETE</span>
                  </div>
                  <div className="p-4 space-y-1.5">
                    {cl.items.map((it) => (
                      <div key={it.id} className="flex items-center gap-2 text-sm">
                        {it.completed ? <CheckCircle2 className="h-4 w-4 text-[#00823B] shrink-0" /> : <Circle className="h-4 w-4 text-[#1C1C1E]/30 shrink-0" />}
                        <span className={it.completed ? "text-[#1C1C1E]/45 line-through" : "text-[#1C1C1E]"}>{it.text}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              );
            })}
          </TabsContent>

          <TabsContent value="files" className="mt-6 space-y-4">
            <label className="inline-block">
              <Button size="sm" asChild data-testid="portal-upload-file-button" className="bg-[#00823B] hover:bg-[#00823B]/90 text-white cursor-pointer rounded-sm active:scale-[0.98] transition-transform duration-150">
                <span><Upload className="h-3.5 w-3.5 mr-1" /> Upload File</span>
              </Button>
              <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
            </label>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <FileSection
                title="Delivered by EnviroWealth"
                icon={ArrowDownToLine}
                files={deliveredFiles}
                emptyText="No deliverables shared yet."
              />
              <FileSection
                title="Received from Client"
                icon={ArrowUpFromLine}
                files={receivedFiles}
                emptyText="Nothing uploaded from your side yet."
              />
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-6 space-y-2">
            {team.length === 0 && <EmptyState text="No team members assigned yet." />}
            {team.map((t) => (
              <div key={t.id} data-testid={`portal-team-row-${t.id}`} className="bg-white border border-[#1C1C1E]/10 rounded-sm p-4 flex items-center justify-between hover:bg-[#1C1C1E]/[0.015] transition-colors duration-200">
                <p className="text-sm font-medium text-[#1C1C1E]">{t.employee_name}</p>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#00823B] bg-[#76C121]/10 border border-[#76C121]/20 px-2 py-1 rounded-sm">{t.role_label}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <Panel>
              <div className="p-4 space-y-3 max-h-[420px] overflow-y-auto">
                {comments.length === 0 && <EmptyState text="No messages yet. Say hello!" />}
                {comments.map((c) => (
                  <div key={c.id} data-testid={`portal-comment-${c.id}`} className={`max-w-[80%] ${c.author_role === "client" ? "ml-auto text-right" : ""}`}>
                    <div className={`inline-block px-3 py-2 rounded-sm text-sm ${c.author_role === "client" ? "bg-[#76C121]/10 text-[#1C1C1E]" : "bg-[#F8F9FA] text-[#1C1C1E]"}`}>
                      {c.message}
                    </div>
                    <p className="font-mono text-[10px] text-[#1C1C1E]/40 mt-1 uppercase tracking-wide">{c.author_name} · {new Date(c.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </Panel>
            <div className="flex gap-2 mt-3">
              <Textarea
                data-testid="portal-message-input"
                placeholder="Type a message to the EnviroWealth team..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={1}
                className="resize-none rounded-sm border-[#1C1C1E]/10"
              />
              <Button data-testid="portal-send-message-button" onClick={sendMessage} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0 rounded-sm active:scale-[0.98] transition-transform duration-150">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function FileSection({ title, icon: Icon, files, emptyText }) {
  return (
    <div className="border border-[#1C1C1E]/10 bg-white rounded-sm flex flex-col">
      <div className="border-b border-[#1C1C1E]/10 p-4 bg-[#F8F9FA] flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-[#00823B]" />
        <p className="font-heading text-sm font-medium text-[#1C1C1E]">{title}</p>
        <span className="font-mono text-[10px] text-[#1C1C1E]/40 ml-auto">{files.length}</span>
      </div>
      {files.length === 0 ? (
        <div className="p-8 flex flex-col items-center justify-center text-center border border-dashed border-[#1C1C1E]/10 m-4 rounded-sm bg-[#F8F9FA]/50">
          <FileX2 className="h-5 w-5 text-[#1C1C1E]/25 mb-2" />
          <p className="font-mono text-[11px] text-[#1C1C1E]/40 uppercase tracking-wide">{emptyText}</p>
        </div>
      ) : (
        <div>
          {files.map((f) => (
            <div key={f.id} data-testid={`portal-file-row-${f.id}`} className="flex items-center justify-between p-4 border-b border-[#1C1C1E]/5 last:border-0 hover:bg-[#F8F9FA] transition-colors duration-200 group">
              <div className="min-w-0">
                <p className="text-sm text-[#1C1C1E] truncate">{f.original_filename}</p>
                <p className="font-mono text-[10px] text-[#1C1C1E]/40 mt-1 uppercase tracking-wide">{f.uploaded_by}</p>
              </div>
              <div className="flex items-center gap-2.5 shrink-0 ml-3">
                <a href={fileUrl(f.file_path)} target="_blank" rel="noreferrer" data-testid={`portal-file-view-${f.id}`} title="View"><Eye className="h-4 w-4 text-[#1C1C1E]/45 hover:text-[#1C1C1E] transition-colors duration-200" /></a>
                <a href={downloadUrl(f.file_path, f.original_filename)} data-testid={`portal-file-download-${f.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Panel({ children, noPadding, testId }) {
  return (
    <div data-testid={testId} className={`border border-[#1C1C1E]/10 bg-white rounded-sm ${noPadding ? "" : ""} overflow-hidden`}>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#00823B]/80 mb-1">{label}</p>
      <p className="text-[#1C1C1E] font-medium text-sm">{value || "—"}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="font-mono text-xs uppercase tracking-wide text-[#1C1C1E]/40 text-center py-10">{text}</p>;
}
